import ColorizeRoundedIcon from '@mui/icons-material/ColorizeRounded';
import { LoadingButton } from '@mui/lab';
import { Box, Card, Grid, IconButton, InputAdornment, InputLabel, Stack, Typography } from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormProvider, RHFTextField, RHFUploadAvatar } from 'src/components/hook-form';
import HttpServices from 'src/services/httpService';
import { imageModeuleUrl, userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ValidationTool from 'src/utils/validationHelper';
import ColorPopover from './branding-color-popover';

const BrandingInfo = () => {
  const { t: translate } = useTranslation();
  const [orgWebsite, setOrgWebsite] = useState('');
  const [orgPrimaryColor, setOrgPrimaryColor] = useState('');
  const [orgSecondaryColor, setOrgSecondaryColor] = useState('');
  const [orgSmallIcon, setOrgSmallIcon] = useState('');
  const [orgLargeIcon, setOrgLargeIcon] = useState('');
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [formSchema, setFormSchema] = useState({});
  const [isSubmitting, setSumbitLoading] = useState(false);
  const maxFileSizeInBytes = 10485760;
  const [fileUploadprogress, setFileUploadprogress] = useState(0);
  const [popoverElement, setPopoverElement] = useState(null);
  const [selectedPopOverData, setSelectedPopOverData] = useState('');
  const { showToast } = useToaster();

  useEffect(() => {
    generateFormModelSchema();
    getBrandingDetails();
  }, []);

  const getBrandingDetails = () => {
    let postProps = {
      url: userModuleURL.getBrandingDetails,
      successCallback,
      failureCallback,
    };
    HttpServices.Get(postProps);
  };
  const successCallback = (data, message) => {
    message && showToast(message, 's');
    setOrgWebsite(data.organization_url);
    setOrgSmallIcon(data.domain_small_icon);
    setOrgLargeIcon(data.domain_large_icon);
    setOrgPrimaryColor(data.org_primary_color);
    setOrgSecondaryColor(data.org_secondary_color);
  };
  const failureCallback = (message) => {
    message && showToast(message, 'e');
    setSumbitLoading(false);
  };
  const generateFormModelSchema = () => {
    let fields = [
      ValidationTool.GenerateSchema(
        'orgWebsite',
        translate('manage_branding.branding.organization_website_url'),
        'TEXT',
        false
      ),
      ValidationTool.GenerateSchema(
        'orgPrimaryColor',
        translate('manage_branding.branding.organization_primary_color'),
        'TEXT',
        false
      ),
      ValidationTool.GenerateSchema(
        'orgSecondaryColor',
        translate('manage_branding.branding.organization_secondary_color'),
        'TEXT',
        false
      ),
      ValidationTool.GenerateSchema(
        'orgSmallIcon',
        translate('manage_branding.branding.organization_small_icon'),
        'TEXT',
        false
      ),
      ValidationTool.GenerateSchema(
        'orgLargeIcon',
        translate('manage_branding.branding.organization_large_icon'),
        'TEXT',
        false
      ),
    ];
    const schema = ValidationTool.GenerateSchemaModel(fields, translate);
    setFormSchema(schema);
  };

  const defaultValues = {
    orgWebsite: orgWebsite || '',
    orgPrimaryColor: orgPrimaryColor || '',
    orgSecondaryColor: orgSecondaryColor || '',
    orgSmallIcon: orgSmallIcon || '',
    orgLargeIcon: orgLargeIcon || '',
  };

  const methods = useForm({
    defaultValues,
  });

  const onSubmit = () => {
    if (orgPrimaryColor && !CommonHelper.CheckForValidHexCode(orgPrimaryColor)) {
      showToast(translate('manage_branding.branding.valid_hex_code_for_primary_color'), 'e');
      return;
    }
    if (orgSecondaryColor && !CommonHelper.CheckForValidHexCode(orgSecondaryColor)) {
      showToast(translate('manage_branding.branding.valid_hex_code_for_secondary_color'), 'e');
      return;
    }
    setSumbitLoading(true);
    let domainProps = {
      organization_url: orgWebsite,
      domain_small_icon: orgSmallIcon,
      domain_large_icon: orgLargeIcon,
      org_primary_color: orgPrimaryColor,
      org_secondary_color: orgSecondaryColor,
    };
    let postProps = {
      url: userModuleURL.updateBrandingDetails,
      body: domainProps,
      successCallback: successFromSubmitCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };
  const successFromSubmitCallback = (data, message) => {
    message && showToast(message, 's');
    setSumbitLoading(false);
    getBrandingDetails();
  };

  const { handleSubmit } = methods;
  const handleSubmitForm = () => {
    let userForm = {
      orgWebsite: orgWebsite,
      orgPrimaryColor: orgPrimaryColor,
      orgSecondaryColor: orgSecondaryColor,
      orgSmallIcon: orgSmallIcon,
      orgLargeIcon: orgLargeIcon,
    };
    let { formValidationErrors, isValidForm } = ValidationTool.FormValidation(formSchema, userForm);
    if (isValidForm) {
      setFormValidationErrors({});
      onSubmit();
    } else setFormValidationErrors(formValidationErrors);
  };
  const handleDropSmallIcon = (acceptedFiles) => {
    if (acceptedFiles?.length > 0) {
      const file = acceptedFiles[0];
      handleFileUpload(file, 'smallicon');
    } else {
      showToast(
        `${translate('common.we_only_support')} JPEG, ${translate('common.and')} PNG ${translate(
          'common.file_formats'
        )}. ${translate('common.selected_file_not_in_format')}`,
        'e'
      );
    }
  };
  const handleDropLargeIcon = (acceptedFiles) => {
    if (acceptedFiles?.length > 0) {
      const file = acceptedFiles[0];
      handleFileUpload(file, 'largeicon');
    } else {
      showToast(
        `${translate('common.we_only_support')} JPEG, ${translate('common.and')} PNG ${translate(
          'common.file_formats'
        )}. ${translate('common.selected_file_not_in_format')}`,
        'e'
      );
    }
  };
  const handleFileUpload = (file, iconType) => {
    let isFileInCorrectFormat = CommonHelper.SupportedImageFormat.some((a) => a.includes(file.type));
    if (isFileInCorrectFormat) {
      if (file.size < maxFileSizeInBytes) {
        setFileUploadprogress(1);
        let formData = new FormData();
        formData.append('fileupload', file);
        axios
          .post(imageModeuleUrl.uploadMedia, formData, {
            headers: HttpServices.GetRequestHeader('form', true),
            onUploadProgress: (data) => {
              let loadedProgress = Math.round((100 * data.loaded) / data.total);
              setFileUploadprogress(loadedProgress > 90 ? 90 : loadedProgress);
            },
          })
          .then((result) => result)
          .then((response) => {
            setFileUploadprogress(100);
            if (response.status === 200 && response.data.http_code === 200)
              fileUploadSuccessCallback(response.data.data, iconType, translate('common.file_uploaded_successfully'));
            else showToast(translate('common.something_went_wrong'), 'e');
            setFileUploadprogress(0);
          })
          .catch((error) => {
            setFileUploadprogress(0);
            showToast(error.toString(), 'e');
          });
      } else showToast(`${translate('brand.max_image_size')} 10MB. ${translate('brand.please_select_below')}`, 'e');
    } else {
      showToast(
        `${translate('common.we_only_support')} JPEG, ${translate('common.and')} PNG ${translate(
          'common.file_formats'
        )}. ${translate('common.selected_file_not_in_format')}`,
        'e'
      );
    }
  };
  const fileUploadSuccessCallback = (data, iconType, message) => {
    if (iconType === 'smallicon') setOrgSmallIcon(data.media_data[0].media_url);
    else setOrgLargeIcon(data.media_data[0].media_url);
  };
  const handleInputChange = (event) => {
    let name = event.target.name;
    let value = event.target.value;
    switch (name) {
      case 'orgWebsite':
        setOrgWebsite(value);
        break;
      case 'orgPrimaryColor':
        value = value ? value.replaceAll(' ', '') : '';
        setOrgPrimaryColor(value);
        break;
      case 'orgSecondaryColor':
        value = value ? value.replaceAll(' ', '') : '';
        setOrgSecondaryColor(value);
        break;
      default:
        break;
    }
  };
  const handleColor = (color, type) => {
    if (type === 'primary') setOrgPrimaryColor(color.hex);
    else setOrgSecondaryColor(color.hex);
  };
  const handleCloseMenu = () => {
    setPopoverElement(null);
  };
  const brandActionProps = {
    popoverElement: popoverElement,
    selectedPopOverData: selectedPopOverData,
    handleCloseMenu,
    handleColor,
    color: orgPrimaryColor,
  };
  const handleOpenMenu = (event, data) => {
    setSelectedPopOverData(data);
    setPopoverElement(event.currentTarget);
  };
  const handleDeleteOrgLargeImage = () => {
    setOrgLargeIcon('');
  };
  const handleDeleteOrgSmallImage = () => {
    setOrgSmallIcon('');
  };

  return (
    <>
      <Helmet>
        <title>{translate('navbar.menu.branding')}</title>
      </Helmet>
      <FormProvider methods={methods} onSubmit={handleSubmit(handleSubmitForm)}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box display="flex" flexDirection="column" height="100%">
              <Card sx={{ py: 2, px: 1, textAlign: 'center', flexGrow: 1 }}>
                <InputLabel style={{ paddingBottom: '0.5rem' }}>
                  {translate('manage_branding.branding.favicon_image')}
                </InputLabel>
                <Box position="relative" display="inline-block">
                  <RHFUploadAvatar
                    name="orgSmallIcon"
                    accept="image/*"
                    onDrop={handleDropSmallIcon}
                    url={orgSmallIcon}
                    handleDeleteProfiles={handleDeleteOrgSmallImage}
                    helperText={
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 2,
                          mx: 'auto',
                          display: 'block',
                          textAlign: 'center',
                          color: 'text.secondary',
                        }}
                      >
                        {translate('brand.allowed')} *.jpeg, *.jpg, *.png, *.gif
                        <br /> {translate('brand.max_size_of')} 5Mb
                      </Typography>
                    }
                  />
                </Box>
                <InputLabel style={{ paddingTop: '1rem', paddingBottom: '0.5rem' }}>
                  {translate('manage_branding.branding.header_logo_image')}
                </InputLabel>
                <Box position="relative" display="inline-block">
                  <RHFUploadAvatar
                    name="orgLargeIcon"
                    accept="image/*"
                    onDrop={handleDropLargeIcon}
                    handleDeleteProfiles={handleDeleteOrgLargeImage}
                    url={orgLargeIcon}
                    helperText={
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 2,
                          mx: 'auto',
                          display: 'block',
                          textAlign: 'center',
                          color: 'text.secondary',
                        }}
                      >
                        {translate('brand.allowed')} *.jpeg, *.jpg, *.png, *.gif
                        <br /> {translate('brand.max_size_of')} 5Mb
                      </Typography>
                    }
                  />
                </Box>
              </Card>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box display="flex" flexDirection="column" height="100%">
              <Card sx={{ p: 3, flexGrow: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    rowGap: 3,
                    columnGap: 2,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  <RHFTextField
                    name="orgWebsite"
                    value={orgWebsite}
                    label={translate('manage_branding.branding.organization_website_url')}
                    onChange={handleInputChange}
                    error={formValidationErrors?.orgWebsite}
                    helperText={formValidationErrors?.orgWebsite}
                  />
                  <RHFTextField
                    name="orgPrimaryColor"
                    value={orgPrimaryColor}
                    label={translate('manage_branding.branding.organization_primary_color')}
                    onChange={handleInputChange}
                    error={formValidationErrors?.orgPrimaryColor}
                    helperText={`${translate('manage_branding.branding.enter_hex_code')} ${translate(
                      'manage_branding.branding.for_example'
                    )} #ffffff`}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="large"
                            color="inherit"
                            onClick={(e) => {
                              handleOpenMenu(e, 'primary');
                            }}
                          >
                            <ColorizeRoundedIcon />
                          </IconButton>
                          {orgPrimaryColor && (
                            <Box
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                boxShadow: 1,
                                backgroundColor: orgPrimaryColor,
                                marginLeft: 1,
                              }}
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />

                  <RHFTextField
                    name="orgSecondaryColor"
                    value={orgSecondaryColor}
                    label={translate('manage_branding.branding.organization_secondary_color')}
                    onChange={handleInputChange}
                    error={formValidationErrors?.orgSecondaryColor}
                    helperText={`${translate('manage_branding.branding.enter_hex_code')} ${translate(
                      'manage_branding.branding.for_example'
                    )} #000000`}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="large"
                            color="green"
                            onClick={(e) => {
                              handleOpenMenu(e, 'secondary');
                            }}
                          >
                            <ColorizeRoundedIcon />
                          </IconButton>
                          {orgSecondaryColor && (
                            <Box
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                boxShadow: 1,
                                backgroundColor: orgSecondaryColor,
                                marginLeft: 1,
                              }}
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Stack spacing={3} alignItems="flex-end" sx={{ mt: 5 }}>
                    <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                      {translate('common.submit')}
                    </LoadingButton>
                  </Stack>
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>
      <ColorPopover {...brandActionProps} />
    </>
  );
};
export default BrandingInfo;
